// Third-party
import React, { ReactElement } from 'react';
import classNames from 'classnames';
import Downshift, { ControllerStateAndHelpers } from 'downshift';
import PerfectScrollbar from 'react-perfect-scrollbar';

interface Props {
  options: JSX.Element[];
  selected: string | number;
  onSelect: (value: any, index: number) => void;
  children: ReactElement | ReactElement[];
  dark?: boolean;
  className?: string;
  dropdownClass?: string;
  isExtended?: boolean;
  disabled?: boolean;
  type?: 'click' | 'hover';
  extraAction?: ReactElement;
  emptyState?: JSX.Element;
  'data-testid'?: string;
  visibleCount?: number;
  upwards?: boolean;
}

interface State {
  isHovered: boolean;
}

class DropdownMenu extends React.Component<Props, State> {
  private menuRef: HTMLDivElement | null = null;

  private containerRef: HTMLDivElement | null = null;

  readonly state: State = {
    isHovered: false,
  };

  static defaultProps = {
    options: [],
    selected: null,
    onSelect: () => {},
  };

  getMenuRef = () => this.menuRef;

  getContainerRef = () => this.containerRef;

  handleMouseEnter = () => {
    this.setState({ isHovered: true });
  };

  handleMouseLeave = () => {
    this.setState({ isHovered: false });
  };

  handleSelect = (selection: any, downshift: ControllerStateAndHelpers<any>) => {
    const { options, onSelect } = this.props;

    this.setState({ isHovered: false }, () => {
      onSelect(options[selection], selection);
      downshift.reset();
    });
  };

  render() {
    const { options, isExtended, dark, children, emptyState, className, disabled, dropdownClass, type, extraAction, selected, visibleCount, upwards, ...rest } =
      this.props;

    const { isHovered } = this.state;
    const classes = classNames(className, 'dropdown-extended', { dark, disabled, upwards });

    return (
      <Downshift onSelect={this.handleSelect}>
        {({ isOpen, openMenu, closeMenu, getItemProps, highlightedIndex, getMenuProps, getInputProps }) => {
          const opened = (isExtended != null ? isExtended : isOpen) || (type === 'hover' && isHovered);

          const handleInputClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            event.stopPropagation();

            if (disabled) {
              return;
            }

            if (opened) {
              closeMenu();
            } else {
              openMenu();
            }
          };

          return (
            <div
              ref={node => {
                this.containerRef = node;
              }}
              className={classes}
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
            >
              <div className="dropdown-child-container" onClick={handleInputClick} data-testid={rest['data-testid']}>
                <div className="input-div" {...getInputProps()}>
                  {children}
                </div>
              </div>

              {opened && (
                <div
                  ref={node => {
                    this.menuRef = node;
                  }}
                  className={dropdownClass}
                >
                  <div className="dropdown-extended-options">
                    {options.length === 0 && emptyState && <div className="dropdown-empty flex-fill">{emptyState}</div>}

                    {options.length > 0 && (
                      <ul {...getMenuProps()}>
                        <PerfectScrollbar style={{ '--visible-count': visibleCount } as React.CSSProperties}>
                          {options.map((option, index) => {
                            const { onClick, ...rest } = getItemProps({
                              index,
                              item: index,
                              className: highlightedIndex === index || selected === index ? 'active' : '',
                            });
                            return (
                              <li
                                key={index}
                                onClick={event => {
                                  event.stopPropagation();
                                  onClick(event);
                                }}
                                {...rest}
                              >
                                {option}
                              </li>
                            );
                          })}
                        </PerfectScrollbar>
                      </ul>
                    )}

                    {extraAction && <div className="extra-action">{extraAction}</div>}
                  </div>
                </div>
              )}
            </div>
          );
        }}
      </Downshift>
    );
  }
}

export default DropdownMenu;
